import { WILDCARD_PERMISSION, type PermissionGrant } from '@optiflow/types';

/**
 * Decides whether a set of grants satisfies a required permission.
 *
 * A grant matches when it is the global wildcard `*`, an exact match, or a domain wildcard such as
 * `customers.*` covering `customers.view`. Matching is intentionally shallow — one level of
 * wildcard only — so that a grant can never widen further than the domain it names.
 */
export function hasPermission(
  grants: readonly PermissionGrant[] | undefined,
  required: string,
): boolean {
  if (!grants || grants.length === 0) return false;

  for (const grant of grants) {
    if (grant === WILDCARD_PERMISSION) return true;
    if (grant === required) return true;
    if (grant.endsWith('.*')) {
      const domain = grant.slice(0, -2);
      if (required.startsWith(`${domain}.`)) return true;
    }
  }

  return false;
}

export function hasAnyPermission(
  grants: readonly PermissionGrant[] | undefined,
  required: readonly string[],
): boolean {
  return required.some((permission) => hasPermission(grants, permission));
}

export function hasAllPermissions(
  grants: readonly PermissionGrant[] | undefined,
  required: readonly string[],
): boolean {
  return required.every((permission) => hasPermission(grants, permission));
}

/** Collapses grants from multiple roles into a stable, de-duplicated list. */
export function mergeGrants(...grantSets: readonly PermissionGrant[][]): PermissionGrant[] {
  const merged = new Set<PermissionGrant>();
  for (const grants of grantSets) {
    for (const grant of grants) merged.add(grant);
  }
  return [...merged].sort();
}
